import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AddIcon from '@mui/icons-material/Add';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { api } from '@/api/client';
import { formatDate, formatDateTime } from '@/utils/format';
import type {
  ClinicianLicense, MedicalJurisdiction, SickNote,
} from '@/types/medical';

/**
 * Sick notes (clinician).
 *
 * Issuing a sick note depends on country-specific registry (PL ZUS ZLA,
 * DE eAU, etc.), so the dialog includes:
 *   - Patient ID + jurisdiction (from clinician's licenses)
 *   - Reason (illness/injury/other)
 *   - Validity range
 *   - Can-walk flag (PL-specific but harmless elsewhere)
 *
 * The backend dispatches to the right registry adapter via
 * `services/sick_notes.issue`.
 */

const REASONS = [
  { code: 'illness', label: 'Illness' },
  { code: 'injury',  label: 'Injury' },
  { code: 'mental',  label: 'Mental health' },
  { code: 'pregnancy', label: 'Pregnancy / childcare' },
  { code: 'other',   label: 'Other' },
];

export function ClinicianSickNotesPage() {
  const [params] = useSearchParams();
  const initialAppointment = params.get('appointment') ?? '';
  const [open, setOpen] = useState(false);

  const q = useApiQuery({
    queryKey: ['clinician', 'sick-notes'],
    queryFn:  () => api.get<SickNote[]>('/api/medical/clinician/sick-notes/'),
  });

  return (
    <>
      <PageHeader
        title="Sick notes"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            New sick note
          </Button>
        }
      />

      <DataState query={q} empty={{ title: 'No sick notes issued yet' }}>
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((s) => <Row key={s.id} note={s} />)}
          </Stack>
        )}
      </DataState>

      {open && (
        <BuilderDialog
          open={open}
          appointmentId={initialAppointment || null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function Row({ note }: { note: SickNote }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <LocalHospitalIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                Patient #{note.patient}
              </Typography>
              <Chip
                size="small"
                label={note.status}
                color={note.status === 'registered' || note.status === 'signed' ? 'primary' : 'default'}
                variant="outlined"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {note.reason} · {note.jurisdiction_code}
              {note.icd_code && ` · ICD ${note.icd_code}`}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" className="tabular" fontWeight={500}>
              {formatDate(note.valid_from)} – {formatDate(note.valid_to)}
            </Typography>
            {note.signed_at && (
              <Typography variant="caption" color="text.secondary" className="tabular">
                signed {formatDateTime(note.signed_at)}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function BuilderDialog({
  open, appointmentId, onClose,
}: {
  open: boolean;
  appointmentId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [patientId, setPatientId] = useState('');
  const [licenseId, setLicenseId] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [reason, setReason] = useState('illness');
  const [icd, setIcd] = useState('');
  const [canWalk, setCanWalk] = useState(true);

  const licensesQ = useApiQuery({
    queryKey: ['clinician', 'licenses'],
    queryFn:  () => api.get<ClinicianLicense[]>('/api/medical/clinician/licenses/'),
    enabled:  open,
  });
  const jurisdictionsQ = useApiQuery({
    queryKey: ['jurisdictions', 'live'],
    queryFn:  () => api.get<MedicalJurisdiction[]>('/api/medical/jurisdictions/'),
    enabled:  open,
  });

  const issueMut = useApiMutation({
    mutationFn: () => api.post<SickNote>('/api/medical/clinician/sick-notes/', {
      patient_id: parseInt(patientId, 10),
      license_id: parseInt(licenseId, 10),
      jurisdiction_id: parseInt(jurisdictionId, 10),
      appointment_id: appointmentId ?? null,
      valid_from: validFrom,
      valid_to: validTo,
      reason,
      icd_code: icd,
      can_walk_outside: canWalk,
    }),
    onSuccess: () => {
      toast.success('Sick note issued.');
      qc.invalidateQueries({ queryKey: ['clinician', 'sick-notes'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid =
    patientId && licenseId && jurisdictionId && validFrom && validTo && reason;

  return (
    <Dialog open={open} onClose={() => !issueMut.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>New sick note</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="Patient ID" required
              value={patientId}
              onChange={(e) => setPatientId(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth select label="Issued under license" required
              value={licenseId} onChange={(e) => setLicenseId(e.target.value)}
            >
              {(licensesQ.data ?? [])
                .filter((l) => l.status === 'active')
                .map((l) => (
                  <MenuItem key={l.id} value={String(l.id)}>
                    {l.jurisdiction_code} · {l.license_number}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth select label="Jurisdiction" required
              value={jurisdictionId} onChange={(e) => setJurisdictionId(e.target.value)}
              helperText="Sick-note registry depends on the patient's country."
            >
              {(jurisdictionsQ.data ?? [])
                .filter((j) => j.is_active && j.launch_status === 'live')
                .map((j) => (
                  <MenuItem key={j.id} value={String(j.id)}>
                    {j.code} — {j.display_name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth select label="Reason" required
              value={reason} onChange={(e) => setReason(e.target.value)}
            >
              {REASONS.map((r) => (
                <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth type="date" label="Valid from" required
              value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth type="date" label="Valid to" required
              value={validTo} onChange={(e) => setValidTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth label="ICD-10 / 11 code"
              value={icd} onChange={(e) => setIcd(e.target.value)}
              placeholder="J11.1"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={canWalk}
                               onChange={(_, v) => setCanWalk(v)} />}
              label="Patient can walk / leave home"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={issueMut.isPending}>Cancel</Button>
        <Button
          variant="contained" disabled={!valid || issueMut.isPending}
          onClick={() => issueMut.mutate()}
        >
          Issue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
